// src/pages/VendorServices.jsx - Fixed Version
import React, { useState, useEffect } from 'react';
import { Header } from '../components';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiFilter, FiClock, FiDownload, FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';

const VendorServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [newService, setNewService] = useState({
    vendorId: '',
    serviceId: '',
    parentServiceId: '',
    name: '',
    price: '',
    priceType: 'fixed',
    duration: '',
    description: '',
    image: null,
    imagePreview: null
  });

  // Fixed API URL - using environment variable
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

  // API for vendor services
  const fetchVendorServices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      if (selectedVendor) params.append('vendorId', selectedVendor);
      if (selectedCategory) params.append('serviceId', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/vendor-services?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        console.log('Services data:', response.data.data); // Debug log
        setServices(response.data.data);
        setUploadError(null);
      }
    } catch (error) {
      console.error('Error fetching vendor services:', error);
      setUploadError('Failed to fetch vendor services');
    } finally {
      setLoading(false);
    }
  };

  // Fetch vendors for dropdown
  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/vendor/list?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        console.log('Vendors data:', response.data.data.vendors); // Debug log
        setVendors(response.data.data.vendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setUploadError('Failed to fetch vendors');
    }
  };

  // Fetch service categories for dropdown
  const fetchServiceCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/service-categories`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        console.log('Categories data:', response.data.data); // Debug log
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
      setUploadError('Failed to fetch service categories');
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchVendors();
    fetchServiceCategories();
  }, []);

  // Fetch services when filters change
  useEffect(() => {
    if (vendors.length > 0 && categories.length > 0) {
      fetchVendorServices();
    }
  }, [selectedVendor, selectedCategory, vendors.length, categories.length]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (vendors.length > 0 && categories.length > 0) {
        fetchVendorServices();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle image upload
  const handleImageChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError("File size must be less than 5MB");
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError("Please select a valid image file");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit && selectedService) {
          setSelectedService({
            ...selectedService,
            newImage: file,
            imagePreview: reader.result
          });
        } else {
          setNewService({
            ...newService,
            image: file,
            imagePreview: reader.result
          });
        }
        setUploadError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Create new service
  const handleCreateService = async (e) => {
    e.preventDefault();
    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('vendorId', newService.vendorId);
      if (newService.parentServiceId) {
        formData.append('parentServiceId', newService.parentServiceId);
      } else {
        formData.append('serviceId', newService.serviceId);
      }
      formData.append('name', newService.name);
      if (newService.price) formData.append('price', newService.price);
      formData.append('priceType', newService.priceType);
      formData.append('duration', newService.duration);
      formData.append('description', newService.description);
      if (newService.image) {
        formData.append('image', newService.image);
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/vendor-services`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setUploadSuccess('Service created successfully!');
        fetchVendorServices();
        setShowAddModal(false);
        setNewService({
          vendorId: '',
          serviceId: '',
          parentServiceId: '',
          name: '',
          price: '',
          priceType: 'fixed',
          duration: '',
          description: '',
          image: null,
          imagePreview: null
        });
        setTimeout(() => setUploadSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error creating vendor service:', error);
      setUploadError(error.response?.data?.message || 'Failed to create vendor service');
    } finally {
      setUploadLoading(false);
    }
  };

  // Update service
  const handleUpdateService = async (e) => {
    e.preventDefault();
    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('name', selectedService.name || '');
      if (selectedService.price != null && selectedService.price !== '') formData.append('price', selectedService.price);
      formData.append('priceType', selectedService.priceType || 'fixed');
      if (selectedService.duration != null && selectedService.duration !== '') formData.append('duration', selectedService.duration);
      formData.append('description', selectedService.description || '');
      formData.append('gender', selectedService.gender || '');

      if (selectedService.newImage) {
        formData.append('image', selectedService.newImage);
      }

      const response = await axios.patch(
        `${API_BASE_URL}/api/admin/vendor-services/${selectedService._id}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setUploadSuccess('Service updated successfully!');
        fetchVendorServices();
        setShowEditModal(false);
        setSelectedService(null);
        setTimeout(() => setUploadSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error updating vendor service:', error);
      setUploadError(error.response?.data?.message || 'Failed to update vendor service');
    } finally {
      setUploadLoading(false);
    }
  };

  // Delete service
  const handleDeleteService = async () => {
    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(
        `${API_BASE_URL}/api/admin/vendor-services/${selectedService._id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        setUploadSuccess('Service deleted successfully!');
        fetchVendorServices();
        setShowDeleteModal(false);
        setSelectedService(null);
        setTimeout(() => setUploadSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error deleting vendor service:', error);
      setUploadError(error.response?.data?.message || 'Failed to delete vendor service');
    } finally {
      setUploadLoading(false);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSelectedVendor('');
    setSelectedCategory('');
    setSearchTerm('');
  };

  // Get vendor name by ID - FIXED VERSION
  const getVendorName = (vendorData) => {
    if (!vendorData) return 'Unknown Vendor';
    
    // Handle if vendorData is already an object with name/store_name
    if (typeof vendorData === 'object' && vendorData.name) {
      return vendorData.store_name || vendorData.name;
    }
    
    // Handle if vendorData is an ID string
    if (typeof vendorData === 'string') {
      const vendor = vendors.find(v => v._id === vendorData);
      return vendor ? (vendor.store_name || vendor.name) : 'Unknown Vendor';
    }
    
    return 'Unknown Vendor';
  };

  // Get category name by ID - FIXED VERSION
  const getCategoryName = (categoryData) => {
    if (!categoryData) return 'Unknown Category';
    
    // Handle if categoryData is already an object with name
    if (typeof categoryData === 'object' && categoryData.name) {
      return categoryData.name;
    }
    
    // Handle if categoryData is an ID string
    if (typeof categoryData === 'string') {
      const category = categories.find(c => c._id === categoryData);
      return category ? category.name : 'Unknown Category';
    }
    
    return 'Unknown Category';
  };

  // Export services data
  const handleExport = () => {
    const csvContent = [
      ['Service Name', 'Vendor', 'Category', 'Price', 'Price Type', 'Duration', 'Description'],
      ...services.map(service => [
        service.name,
        getVendorName(service.vendorId),
        getCategoryName(service.serviceId),
        service.price,
        service.priceType || 'fixed',
        service.duration,
        service.description
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor_services_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-card p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Vendor Services Management</h2>
          <div className="flex space-x-2">
            <button
              onClick={handleExport}
              disabled={services.length === 0}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                services.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <FiDownload className="mr-2" />
              Export CSV ({services.length})
            </button>
            <button
              onClick={() => {
                fetchVendors();
                fetchServiceCategories();
                fetchVendorServices();
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <FiPlus className="mr-2" />
              Add Service
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {uploadSuccess && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {uploadSuccess}
          </div>
        )}
        {uploadError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {uploadError}
          </div>
        )}

        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center w-full md:w-auto space-x-2">
            <div className="relative flex-grow md:w-64">
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FiFilter size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Vendor
                </label>
                <select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                >
                  <option value="">All Vendors</option>
                  {vendors.map((vendor) => (
                    <option key={vendor._id} value={vendor._id}>
                      {vendor.store_name || vendor.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Services List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading services...</span>
        </div>
      ) : services.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-card p-12 text-center">
          <p className="text-gray-500 text-lg mb-4">No services found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Your First Service
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div key={service._id} className="bg-white rounded-3xl shadow-card border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative h-48">
                {service.image ? (
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-sm">
                    No Image
                  </div>
                )}
                <div className="absolute top-2 right-2 flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedService(service);
                      setShowEditModal(true);
                    }}
                    className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                  >
                    <FiEdit2 className="text-blue-600" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedService(service);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                  >
                    <FiTrash2 className="text-red-600" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="mb-2 flex flex-wrap gap-2">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    {getCategoryName(service.serviceId)}
                  </span>
                  <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                    {getVendorName(service.vendorId)}
                  </span>
                  {service.parentServiceId && (
                    <span className="inline-block bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                      ↳ Sub-service
                    </span>
                  )}
                  {service.hasChildren && (
                    <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      Has sub-services
                    </span>
                  )}
                </div>
                {service.parentServiceId && (
                  <p className="text-xs text-amber-700 mb-1 italic">
                    Under: {services.find(s => s._id === service.parentServiceId?.toString())?.name || 'Parent service'}
                  </p>
                )}
                <h3 className="text-lg font-bold text-gray-800 mb-1">{service.name}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{service.description}</p>
                <div className="flex justify-between items-center">
                  <div className="flex items-center text-green-700 font-bold">
                    {service.price != null
                      ? <>₹{service.price}{service.priceType === 'onwards' && <span className="text-xs font-normal text-gray-500 ml-1">onwards</span>}</>
                      : <span className="text-xs font-normal text-gray-400">Price from sub-services</span>
                    }
                  </div>
                  <div className="flex items-center text-gray-600">
                    <FiClock className="mr-1" />
                    {service.duration || '—'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Service Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Add New Service</h3>
              <form onSubmit={handleCreateService}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor *
                    </label>
                    <select
                      required
                      value={newService.vendorId}
                      onChange={(e) => setNewService({ ...newService, vendorId: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                    >
                      <option value="">Select Vendor</option>
                      {vendors.map((vendor) => (
                        <option key={vendor._id} value={vendor._id}>
                          {vendor.store_name || vendor.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Service Category — hidden if a parent service is selected */}
                  {!newService.parentServiceId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Category *
                      </label>
                      <select
                        required={!newService.parentServiceId}
                        value={newService.serviceId}
                        onChange={(e) => setNewService({ ...newService, serviceId: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category._id} value={category._id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Optional parent service — if set, this becomes a sub-service */}
                  <div className={newService.parentServiceId ? 'col-span-2' : ''}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent Service <span className="text-gray-400 font-normal">(optional — leave blank for root service)</span>
                    </label>
                    <select
                      value={newService.parentServiceId}
                      onChange={(e) => setNewService({ ...newService, parentServiceId: e.target.value, serviceId: '' })}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                    >
                      <option value="">None (root-level service)</option>
                      {services
                        .filter(s => s.vendorId?._id === newService.vendorId || s.vendorId === newService.vendorId)
                        .filter(s => !s.parentServiceId)
                        .map((s) => (
                          <option key={s._id} value={s._id}>{s.name}</option>
                        ))}
                    </select>
                    {newService.parentServiceId && (
                      <p className="text-xs text-amber-600 mt-1">↳ This will be created as a sub-service. Category is inherited from parent.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={newService.name}
                      onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                      placeholder="Enter service name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (₹) <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={newService.price}
                      onChange={(e) => setNewService({ ...newService, price: e.target.value.replace(/[^0-9.]/g, '') })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                      placeholder="Leave blank if using sub-options"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price Type
                    </label>
                    <div className="flex rounded-md overflow-hidden border border-gray-300">
                      <button
                        type="button"
                        onClick={() => setNewService({ ...newService, priceType: 'fixed' })}
                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                          newService.priceType === 'fixed'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Fixed
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewService({ ...newService, priceType: 'onwards' })}
                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                          newService.priceType === 'onwards'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Onwards
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {newService.priceType === 'fixed' ? 'Shows as ₹500' : 'Shows as ₹500 onwards'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration *
                    </label>
                    <input
                      type="text"
                      required
                      value={newService.duration}
                      onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                      placeholder="e.g. 30 min or 1 hour"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    required
                    value={newService.description}
                    onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                    rows="3"
                    placeholder="Enter service description"
                  ></textarea>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Image
                  </label>
                  <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 mb-2">
                    {newService.imagePreview ? (
                      <div className="relative w-full max-w-xs">
                        <img
                          src={newService.imagePreview}
                          alt="Preview"
                          className="max-h-48 max-w-full object-contain rounded"
                        />
                        <button
                          type="button"
                          onClick={() => setNewService({ ...newService, image: null, imagePreview: null })}
                          className="absolute top-0 right-0 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <label className="cursor-pointer">
                          <div className="text-blue-600 hover:text-blue-700">
                            <p>Click to upload image</p>
                            <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageChange(e)}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setNewService({
                        vendorId: '',
                        serviceId: '',
                        name: '',
                        price: '',
                        priceType: 'fixed',
                        duration: '',
                        description: '',
                        image: null,
                        imagePreview: null
                      });
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadLoading}
                    className={`px-4 py-2 rounded-md ${
                      uploadLoading
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'text-white bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {uploadLoading ? 'Adding...' : 'Add Service'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {showEditModal && selectedService && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Edit Service</h3>
              <form onSubmit={handleUpdateService}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor
                    </label>
                    <input
                      type="text"
                      disabled
                      value={getVendorName(selectedService.vendorId)}
                      className="w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Category
                    </label>
                   <input
                     type="text"
                     disabled
                     value={getCategoryName(selectedService.serviceId)}
                     className="w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md"
                   />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Service Name
                   </label>
                   <input
                     type="text"
                     required
                     value={selectedService.name}
                     onChange={(e) => setSelectedService({ ...selectedService, name: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                     placeholder="Enter service name"
                   />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Price (₹)
                   </label>
                   <input
                     type="text"
                     inputMode="numeric"
                     required
                     value={selectedService.price}
                     onChange={(e) => setSelectedService({ ...selectedService, price: e.target.value.replace(/[^0-9.]/g, '') })}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                     placeholder="Enter price"
                   />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Price Type
                   </label>
                   <div className="flex rounded-md overflow-hidden border border-gray-300">
                     <button
                       type="button"
                       onClick={() => setSelectedService({ ...selectedService, priceType: 'fixed' })}
                       className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                         (selectedService.priceType || 'fixed') === 'fixed'
                           ? 'bg-blue-600 text-white'
                           : 'bg-white text-gray-700 hover:bg-gray-50'
                       }`}
                     >
                       Fixed
                     </button>
                     <button
                       type="button"
                       onClick={() => setSelectedService({ ...selectedService, priceType: 'onwards' })}
                       className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                         selectedService.priceType === 'onwards'
                           ? 'bg-blue-600 text-white'
                           : 'bg-white text-gray-700 hover:bg-gray-50'
                       }`}
                     >
                       Onwards
                     </button>
                   </div>
                   <p className="text-xs text-gray-500 mt-1">
                     {(selectedService.priceType || 'fixed') === 'fixed' ? 'Shows as ₹500' : 'Shows as ₹500 onwards'}
                   </p>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Duration
                   </label>
                   <input
                     type="text"
                     required
                     value={selectedService.duration}
                     onChange={(e) => setSelectedService({ ...selectedService, duration: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                     placeholder="e.g. 30m or 1h"
                   />
                 </div>
               </div>
               
               <div className="mb-4">
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   Description
                 </label>
                 <textarea
                   required
                   value={selectedService.description}
                   onChange={(e) => setSelectedService({ ...selectedService, description: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                   rows="3"
                   placeholder="Enter service description"
                 ></textarea>
               </div>
               
               <div className="mb-4">
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   Service Image
                 </label>
                 <div className="flex items-center justify-center border border-gray-300 rounded-lg p-2 mb-2">
                   <img
                     src={selectedService.imagePreview || selectedService.image}
                     alt={selectedService.name}
                     className="max-h-48 object-contain rounded"
                   />
                 </div>
                 <div className="mt-2">
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Change Image (Optional)
                   </label>
                   <div className="flex items-center">
                     <label className="cursor-pointer px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                       <span>Upload New Image</span>
                       <input
                         type="file"
                         accept="image/*"
                         className="hidden"
                         onChange={(e) => handleImageChange(e, true)}
                       />
                     </label>
                     {selectedService.imagePreview && (
                       <button
                         type="button"
                         onClick={() => setSelectedService({
                           ...selectedService,
                           newImage: null,
                           imagePreview: null
                         })}
                         className="ml-2 px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
                       >
                         Cancel
                       </button>
                     )}
                   </div>
                 </div>
               </div>
               
               <div className="flex justify-end space-x-3 mt-6">
                 <button
                   type="button"
                   onClick={() => {
                     setShowEditModal(false);
                     setSelectedService(null);
                   }}
                   className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                 >
                   Cancel
                 </button>
                 <button
                   type="submit"
                   className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                 >
                   Save Changes
                 </button>
               </div>
             </form>
           </div>
         </div>
       </div>
     )}
     
     {/* Delete Confirmation Modal */}
     {showDeleteModal && selectedService && (
       <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-3xl shadow-xl w-full max-w-md">
           <div className="p-6">
             <h3 className="text-xl font-bold mb-4">Delete Service</h3>
             <p className="mb-4 text-gray-700">
               Are you sure you want to delete <strong>{selectedService.name}</strong>? This action cannot be undone.
             </p>
             
             <div className="flex justify-end space-x-3">
               <button
                 type="button"
                 onClick={() => {
                   setShowDeleteModal(false);
                   setSelectedService(null);
                 }}
                 className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
               >
                 Cancel
               </button>
               <button
                 type="button"
                 onClick={handleDeleteService}
                 className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
               >
                 Delete
               </button>
             </div>
           </div>
         </div>
       </div>
     )}
   </div>
 );
};

export default VendorServices;